import { toast } from "react-toastify";
import apiClient from "../../apis/api-client";
import { getFriendlyErrorMessage } from "../../util/error-message-mapper";
import { default as tenantManagementClient } from "../../apis/tenant-management/tenant-management-client";
import type { AuthType } from "../../domain/auth/auth";
import {
  FileUploadResponse,
  Procedure,
  set,
  get,
  buildSmartApplyProcedureUrlPath,
  ProcedureDetail,
  initializeProcedure,
  StatusValue,
  Status,
  isProcedure,
  Jurisdiction,
  ProcValidation,
  mapFileToRequest,
  SmartApplyValidationResult,
} from "../../domain/proc-management";
import { BaseApplicationService } from "../../domain/base/base-application-service";
import {
  isValidProcManagementFileSize,
  MAX_FILE_SIZE_IN_PROC_MANAGEMENT,
  updateAsyncValidationTarget,
  validateProcedure,
  validateSmartApply,
} from "../../../application/domain/proc-management/util/procedure-validate";
import {
  hasAccessFor,
  shouldAutomaticallySetGroupIdFromAuthCtx,
} from "../../domain/proc-management/access-control";
import {
  isNotFoundError,
  isPermissionError,
  isSmartApplyValidationError,
  isValidationError,
  isVersionOutdatedError,
  ReportNonRequiredError,
  reportProcManagementRelatedError,
} from "../../domain/proc-management/util/error";
import {
  ProcedureEntity,
  PublishStatus,
} from "../../domain/procedures/procedure-entity";
import { gqlClient, gqlHeader, runGqlQueryV2 } from "@graffer-inc/smart-apply";

const statusActionMap = {
  [Status.Published]: apiClient.procManagement.publish,
  [Status.Draft]: apiClient.procManagement.unPublish,
  [Status.Archived]: apiClient.procManagement.archive,
} as const;

export type ContextProps = {
  procedure: ProcedureDetail | Procedure;
  smartApply?: Pick<ProcedureEntity, "id" | "publishStatus" | "orgId">;
  onSave: () => Promise<void>;
  onChange: (path: (string | number)[], value: any) => void;
  onFileAttached: (
    path: (string | number)[],
    event: React.ChangeEvent<HTMLInputElement>,
  ) => Promise<void>;
  isUpdating: boolean;
  fileUploading: Record<string, any>;
  getFileUploading: (path: (string | number)[]) => boolean;
  find: (procId: string) => Promise<void>;
  fetchSmartApplyStatus: (smartApplyId: string) => Promise<void>;
  clearSmartApplyStatus: () => void;
  publish: () => Promise<void>;
  unPublish: () => Promise<void>;
  archive: () => Promise<void>;
  copy: () => Promise<string | undefined>;
  publishProcAndSmartApply: () => Promise<void>;
  attachGroupToProc: (groupId: string) => Promise<void>;
  canEditGroupId: () => boolean;
  canManipulateProc: () => boolean;

  organizationSlug?: string;
  setOrganization: (id: string) => void;

  authCtx: AuthType["data"];
  setAuthCtx: (authCtx: ContextProps["authCtx"]) => void;

  validationErrors: ProcValidation;
  shouldDisplayError: boolean;

  // 選択されているスマート申請が紐付け可能なものかを確認する。
  // proc-managementのProcedureのvalidationとは、
  // 観点やライフサイクルが異なるので分離している。
  smartApplyValidationResult: SmartApplyValidationResult;
};

export class BaseProcManagementUpsertService<
  P extends Record<string, any>,
> extends BaseApplicationService<P & ContextProps> {
  constructor(rest: P & Partial<ContextProps>) {
    super();
    this.state = {
      procedure: initializeProcedure(),
      smartApply: undefined,
      onSave: this.handleSave.bind(this),
      onChange: this.handleChange.bind(this),
      onFileAttached: this.handleFileAttached.bind(this),
      isUpdating: false,
      fileUploading: {},
      getFileUploading: this.getFileUploading.bind(this),
      find: this.find.bind(this),
      fetchSmartApplyStatus: this.fetchSmartApplyStatus.bind(this),
      clearSmartApplyStatus: this.clearSmartApplyStatus.bind(this),
      publish: this.publish.bind(this),
      unPublish: this.unPublish.bind(this),
      archive: this.archive.bind(this),
      copy: this.copy.bind(this),
      publishProcAndSmartApply: this.publishProcAndSmartApply.bind(this),
      attachGroupToProc: this.attachGroupToProc.bind(this),
      canEditGroupId: this.canEditGroupId.bind(this),
      canManipulateProc: this.canManipulateProc.bind(this),

      organizationSlug: undefined,
      setOrganization: this.setOrganization.bind(this),

      authCtx: undefined,
      setAuthCtx: this.setAuthCtx.bind(this),

      validationErrors: { errorCounts: 0 },
      shouldDisplayError: false,

      smartApplyValidationResult: {},

      ...rest,
    };
  }

  async find(procId: string) {
    try {
      const { data } = await apiClient.procManagement.find(procId);
      this.setState((prevState) => {
        return {
          ...prevState,
          procedure: data,
        };
      });
    } catch (error) {
      reportProcManagementRelatedError(error);
      if (isNotFoundError(error)) {
        toast.error(
          `手続きデータを見つけることができませんでした。URLやIDが間違いないかご確認ください。 id: ${procId}`,
        );
        return;
      }

      if (isPermissionError(error)) {
        toast.error(`手続きデータを取得する権限がありません。 id: ${procId}`);
        return;
      }

      reportProcManagementRelatedError(error);
      const friendlyMessage = getFriendlyErrorMessage(error);
      toast.error(`手続きデータの取得に失敗しました: ${friendlyMessage}`);
      return;
    }
  }

  async fetchSmartApplyStatus(smartApplyId: string) {
    let smartApply: Pick<ProcedureEntity, "id" | "publishStatus" | "orgId">;
    try {
      // 手続きマスタとスマート申請の管理グループが一致していないケースであってもスマート申請の公開ステータスを取得するため、
      // 権限チェックのあるgetByIdではなく権限チェックのないgetLiveByIdを呼び出している。
      smartApply = (await apiClient.fetchLivingProcedure(smartApplyId)).data;
    } catch (e) {
      let publishStatus: PublishStatus;
      if (e?.response?.data?.code === "not_found.procedure_finished_publish") {
        publishStatus = "UNPUBLISHED";
      } else if (
        e?.response?.data?.code === "not_found.procedure_before_publish"
      ) {
        publishStatus = "BEFORE_PUBLISH";
      } else if (e?.response?.data?.code === "not_found") {
        publishStatus = "DRAFT";
      } else {
        const friendlyMessage = getFriendlyErrorMessage(e);
        toast.error(`スマート申請の取得に失敗しました: ${friendlyMessage}`);
        reportProcManagementRelatedError(e);
        return;
      }
      smartApply = {
        id: smartApplyId,
        publishStatus: publishStatus,
        orgId: this.state.procedure.jurisdiction.organizationId,
      };
    }
    this.setState((prevState) => {
      return {
        ...prevState,
        smartApply: smartApply,
      };
    });
  }

  clearSmartApplyStatus() {
    this.setState((prev) => {
      return {
        ...prev,
        smartApply: undefined,
      };
    });
  }

  async handleSave() {
    const { procedure, organizationSlug } = this.state;
    if (!procedure || !organizationSlug || !this.canManipulateProc()) {
      return;
    }
    const validationErrors = validateProcedure(procedure);
    const smartApplyValidationResult = await validateSmartApply(procedure);
    if (
      this.hasValidationError(
        procedure,
        validationErrors,
        smartApplyValidationResult,
      )
    ) {
      this.displayValidationError(
        procedure,
        validationErrors,
        smartApplyValidationResult,
      );
      return;
    }
    this.setState((prev) => {
      return {
        ...prev,
        isUpdating: true,
      };
    });

    let newProc: Procedure;
    const operation = isProcedure(procedure) ? "更新" : "作成";
    try {
      await this._preUpdateHooks(procedure, organizationSlug);
      if (isProcedure(procedure)) {
        newProc = (await apiClient.procManagement.update(procedure)).data;
      } else {
        newProc = (await apiClient.procManagement.create(procedure)).data;
      }
    } catch (error) {
      if (isValidationError(error)) {
        toast.error(
          `手続きデータの${operation}に失敗しました。必要な入力が完了しているか、今一度ご確認ください。`,
        );
      } else if (isVersionOutdatedError(error)) {
        toast.error(
          `手続きデータの${operation}に失敗しました。画面を更新して再度実行してください。`,
        );
      } else {
        reportProcManagementRelatedError(error);
        const friendlyMessage = getFriendlyErrorMessage(error);
        toast.error(
          `手続きデータの${operation}に失敗しました: ${friendlyMessage}`,
        );
      }
      this.setState((prev) => {
        return {
          ...prev,
          isUpdating: false,
        };
      });
      return;
    }

    this.setState((prev) => {
      return {
        ...prev,
        procedure: newProc,
        isUpdating: false,
      };
    });
    toast.success(`手続きデータを${operation}しました。`);
  }

  private _preUpdateHooks(
    procedure: ContextProps["procedure"],
    organizationSlug: string,
  ) {
    return Promise.all([
      this._fetchSmartApplyProcedureInfo(procedure, organizationSlug),
    ]);
  }

  private async _fetchSmartApplyProcedureInfo(
    procedure: ContextProps["procedure"],
    organizationSlug: string,
  ) {
    const id = procedure.onlineInfo?.grafferSmartApplyInfo?.procedureId;
    if (!id) {
      return;
    }

    let proc: ProcedureEntity;
    let currentPath: string | undefined;
    try {
      proc = (await apiClient.fetchProcedure(id)).data;
      const res = await runGqlQueryV2(() =>
        gqlClient.currentCustomPaths(
          { orgId: proc.orgId, procedureId: id },
          gqlHeader("q"),
        ),
      );
      if (res.error) {
        throw res.error;
      }
      if (res.data) {
        currentPath = res.data.currentCustomPaths.currentCustomPath?.customPath;
      }
    } catch (error) {
      if (isNotFoundError(error)) {
        throw new ReportNonRequiredError(
          `紐づけるスマート申請データが見つかりませんでした。入力間違いがないかご確認ください。スマート申請手続きID: ${id}`,
        );
      }

      if (isPermissionError(error)) {
        throw new ReportNonRequiredError(
          `紐づけるスマート申請データを取得する権限がありません。スマート申請手続きID: ${id}`,
        );
      }

      reportProcManagementRelatedError(error);
      const message = error.response?.data?.msg ?? error.message;
      throw new ReportNonRequiredError(
        `スマート申請データを取得できませんでした: ${message}`,
      );
    }

    set(
      procedure,
      ["onlineInfo", "grafferSmartApplyInfo", "procedurePath"],
      buildSmartApplyProcedureUrlPath(proc.id, organizationSlug, currentPath),
    );
    set(
      procedure,
      ["onlineInfo", "grafferSmartApplyInfo", "procedureName"],
      proc.name,
    );
  }

  async handleChange(path: (string | number)[], value: any) {
    const { procedure } = this.state;
    set(procedure, path, value === "" ? null : value);
    const next = { ...procedure };
    const validationErrors = validateProcedure(procedure);
    this.setState((prev) => {
      return {
        ...prev,
        procedure: next,
        validationErrors: validationErrors,
      };
    });

    if (updateAsyncValidationTarget(path, value)) {
      const smartApplyValidationResult = await validateSmartApply(procedure);
      this.setState((prev) => {
        return {
          ...prev,
          smartApplyValidationResult,
        };
      });
    }
  }

  async handleFileAttached(
    path: (string | number)[],
    event: React.ChangeEvent<HTMLInputElement>,
  ) {
    const { organizationSlug, procedure, fileUploading } = this.state;
    if (!organizationSlug || !procedure) {
      return;
    }

    const { files } = event.target;
    if (!files || !files[0]) {
      toast.error("ファイルが見つかりません。");
      return;
    }

    if (!isValidProcManagementFileSize(files[0])) {
      toast.error(
        `${
          MAX_FILE_SIZE_IN_PROC_MANAGEMENT / 1024 / 1024
        }MB以上のファイルは登録いただけません。`,
      );
      return;
    }

    set(fileUploading, path, true);
    this.setState((prev) => {
      return { ...prev, fileUploading: { ...fileUploading } };
    });
    let res: FileUploadResponse;
    try {
      res = await this.uploadFile(files[0], organizationSlug);
    } catch (error) {
      reportProcManagementRelatedError(error);
      set(fileUploading, path, false);
      this.setState((prev) => {
        return { ...prev, fileUploading: { ...fileUploading } };
      });
      const message = error.response?.data?.msg ?? error.message;
      toast.error(`ファイルをアップロードできませんでした: ${message}`);
      return;
    }

    const { name: fileName, path: filePath, type } = res;
    set(procedure, [...path, "fileName"], fileName);
    set(procedure, [...path, "filePath"], filePath);
    set(procedure, [...path, "type"], type);
    set(fileUploading, path, false);
    const validationErrors = validateProcedure(procedure);
    this.setState((prev) => {
      return {
        ...prev,
        procedure: { ...procedure },
        fileUploading: { ...fileUploading },
        validationErrors: validationErrors,
      };
    });
    toast.success("ファイルをアップロードしました。");
  }

  private async uploadFile(file: File, organizationSlug: string) {
    if (typeof file.type === "string" && file.type.length > 0) {
      return (
        await apiClient.procManagement.uploadFileWithMultipart({
          file,
          json: {
            name: file.name,
            type: file.type,
            organizationSlug,
          },
        })
      ).data;
    }

    const fileUploadRequest = await mapFileToRequest(file, organizationSlug);
    return (await apiClient.procManagement.uploadFile(fileUploadRequest)).data;
  }

  getFileUploading(path: (string | number)[]) {
    const { fileUploading } = this.state;
    const state = get(fileUploading, path);
    return typeof state === "boolean" && state;
  }

  async publish() {
    const proc = this.state.procedure;
    const validationErrors = validateProcedure(proc);
    const smartApplyValidationResult = await validateSmartApply(proc);
    if (
      this.hasValidationError(
        proc,
        validationErrors,
        smartApplyValidationResult,
        true,
      )
    ) {
      this.displayValidationError(
        proc,
        validationErrors,
        smartApplyValidationResult,
        true,
      );
      return;
    }
    return this.updateStatus(Status.Published);
  }

  async unPublish() {
    return this.updateStatus(Status.Draft);
  }

  async archive() {
    return this.updateStatus(Status.Archived);
  }

  async copy(name: string): Promise<string | undefined> {
    const { procedure } = this.state;
    if (!isProcedure(procedure)) {
      toast.error("コピー元の手続きがありません。");
      return;
    }

    try {
      const { data: copied } = await apiClient.procManagement.copy(
        procedure.id,
        name,
      );
      toast.success("手続きをコピーしました。");
      return copied.id;
    } catch (e) {
      reportProcManagementRelatedError(e);
      const friendlyMessage = getFriendlyErrorMessage(e);
      toast.error(`手続きのコピーに失敗しました: ${friendlyMessage}`);
      return;
    }
  }

  // copied the logic inside publish() method,
  // because need to handle exception to prevent publishing smart apply
  // when getting failure during publishing proc mst.
  async publishProcAndSmartApply() {
    const { procedure } = this.state;
    const validationErrors = validateProcedure(procedure);
    const smartApplyValidationResult = await validateSmartApply(procedure);
    if (
      this.hasValidationError(
        procedure,
        validationErrors,
        smartApplyValidationResult,
        true,
      )
    ) {
      this.displayValidationError(
        procedure,
        validationErrors,
        smartApplyValidationResult,
        true,
      );
      return;
    }
    if (!isProcedure(procedure)) {
      toast.error("更新対象の手続きマスタがありません。");
      return;
    }
    const onlineInfo = procedure.onlineInfo;
    if (!onlineInfo || !onlineInfo.grafferSmartApplyInfo) {
      toast.error(`公開対象のスマート申請が指定されていません。`);
      return;
    }
    let newProc: Procedure;
    try {
      newProc = (
        await statusActionMap[Status.Published](procedure.id, {
          updatedAt: procedure.updatedAt,
        })
      ).data;
    } catch (error) {
      this.handleStatusUpdateError(error);
      return;
    }
    this.setState((prev) => {
      return {
        ...prev,
        procedure: newProc,
      };
    });

    try {
      await apiClient.publishProcedure(
        onlineInfo.grafferSmartApplyInfo.procedureId,
      );
      toast.success("手続きマスタとスマート申請を公開しました。");
    } catch (e) {
      if (isSmartApplyValidationError(e)) {
        toast.error(
          `手続きマスタを公開しましたが、スマート申請は入力不備があるため公開できませんでした。`,
        );
      } else if (isPermissionError(e)) {
        toast.error(
          `手続きマスタを公開しましたが、スマート申請は権限がないため公開できませんでした。`,
        );
      } else if (isNotFoundError(e)) {
        toast.error(
          `手続きマスタを公開しましたが、スマート申請は公開できませんでした。`,
        );
        reportProcManagementRelatedError(e);
      } else {
        const friendlyMessage = getFriendlyErrorMessage(e);
        toast.error(
          `手続きマスタを公開しましたが、スマート申請は公開できませんでした: ${friendlyMessage}`,
        );
        reportProcManagementRelatedError(e);
      }
    }
  }

  private async updateStatus(status: StatusValue) {
    const { procedure } = this.state;
    if (!isProcedure(procedure)) {
      toast.error("更新対象の手続きがありません。");
      return;
    }

    let newProc: Procedure;
    try {
      newProc = (
        await statusActionMap[status](procedure.id, {
          updatedAt: procedure.updatedAt,
        })
      ).data;
    } catch (error) {
      this.handleStatusUpdateError(error);
      return;
    }

    this.setState((prev) => {
      return {
        ...prev,
        procedure: newProc,
      };
    });
    toast.success("ステータスを更新しました。");
  }

  private handleStatusUpdateError(error: any) {
    if (isVersionOutdatedError(error)) {
      toast.error(
        "ステータスの更新に失敗しました。画面を更新して再度実行してください。",
      );
      return;
    }
    if (isValidationError(error)) {
      toast.error(
        "ステータスの更新に失敗しました。必要な入力が完了しているか、今一度ご確認ください。",
      );
      return;
    }
    reportProcManagementRelatedError(error);
    const friendlyMessage = getFriendlyErrorMessage(error);
    toast.error(`ステータスの更新に失敗しました: ${friendlyMessage}`);
  }

  async attachGroupToProc(groupId: string) {
    if (!this.canEditGroupId()) {
      toast.error("管理グループの設定は、下書きのときでないと更新できません。");
      return;
    }

    await this.updateJurisdiction({
      ...this.state.procedure.jurisdiction,
      groupId,
    });
  }

  private async updateJurisdiction(body: Jurisdiction) {
    const { procedure } = this.state;

    // 新規作成時はstateの更新のみ
    if (!isProcedure(procedure)) {
      this.setState((prev) => {
        return {
          ...prev,
          procedure: {
            ...this.state.procedure,
            jurisdiction: body,
          },
        };
      });
      return;
    }

    try {
      const { jurisdiction, updatedAt } = (
        await apiClient.procManagement.updateJurisdiction(procedure.id, {
          jurisdiction: body,
          updatedAt: procedure.updatedAt,
        })
      ).data;
      this.setState((prev) => {
        return {
          ...prev,
          procedure: {
            ...this.state.procedure,
            jurisdiction: jurisdiction,
            updatedAt: updatedAt,
          },
        };
      });
      toast.success("管理グループの設定を更新しました。");
    } catch (error) {
      if (isVersionOutdatedError(error)) {
        toast.error(
          `管理グループの設定に失敗しました。画面を更新して再度実行してください。`,
        );
        return;
      }
      reportProcManagementRelatedError(error);
      const message = error.response?.data?.msg ?? error.message;
      toast.error(`管理グループの設定に失敗しました。 message: ${message}`);
      return;
    }
  }

  canEditGroupId() {
    const { authCtx } = this.state;
    return (
      this.canManipulateProc() &&
      ((authCtx?.groupIds && authCtx?.groupIds.length > 1) ||
        !shouldAutomaticallySetGroupIdFromAuthCtx(authCtx)) &&
      this.state.procedure.contentStatus === Status.Draft
    );
  }

  canManipulateProc() {
    const { authCtx, procedure } = this.state;
    return hasAccessFor(authCtx, procedure);
  }

  async setOrganization(organizationId: string) {
    try {
      const { data } =
        await tenantManagementClient.fetchOrganization(organizationId);
      const { procedure } = this.state;
      const newProc = isProcedure(procedure)
        ? procedure
        : {
            ...procedure,
            jurisdiction: {
              ...procedure.jurisdiction,
              organizationId,
            },
          };
      this.setState((prev) => {
        return {
          ...prev,
          organizationSlug: data.slug,
          procedure: newProc,
        };
      });
    } catch (error) {
      reportProcManagementRelatedError(error);
      const friendlyMessage = getFriendlyErrorMessage(error);
      toast.error(`組織データの取得に失敗しました: ${friendlyMessage}`);
    }
  }

  setAuthCtx(authCtx: ContextProps["authCtx"]) {
    const { procedure } = this.state;
    const newProc = shouldAutomaticallySetGroupIdFromAuthCtx(authCtx)
      ? {
          ...procedure,
          jurisdiction: {
            ...procedure.jurisdiction,
            groupId: authCtx?.groupIds[0],
          },
        }
      : procedure;

    this.setState((prev) => {
      return {
        ...prev,
        authCtx,
        procedure: newProc,
      };
    });
  }

  hasValidationError(
    proc: ProcedureDetail | Procedure,
    validationErrors: ProcValidation,
    smartApplyValidationResult: SmartApplyValidationResult,
    isPublishOperation?: boolean,
  ) {
    if (!proc.jurisdiction.groupId || proc.jurisdiction.groupId.trim() === "") {
      return true;
    }
    const isSaveForPublishedProc =
      isProcedure(proc) && proc.contentStatus === "PUBLISHED";
    if (!proc.name || proc.name === "") {
      return true;
    }
    if (typeof smartApplyValidationResult.error === "string") {
      return true;
    }
    if (
      validationErrors.errorCounts !== 0 &&
      (isPublishOperation || isSaveForPublishedProc)
    ) {
      return true;
    }
    return false;
  }

  displayValidationError(
    proc: ProcedureDetail | Procedure,
    validationErrors: ProcValidation,
    smartApplyValidationResult: SmartApplyValidationResult,
    isPublishOperation?: boolean,
  ) {
    if (!proc.jurisdiction.groupId || proc.jurisdiction.groupId.trim() === "") {
      toast.error("管理グループを設定してください");
      return;
    }
    if (!proc.name || proc.name === "") {
      toast.error("手続き名を入力してください");
      return;
    }
    if (typeof smartApplyValidationResult.error === "string") {
      toast.error(smartApplyValidationResult.error);
      return;
    }
    if (
      validationErrors.errorCounts !== 0 &&
      (isPublishOperation || isProcedure(proc))
    ) {
      toast.error("入力内容に誤りがあります");
      this.setState((prev) => {
        return {
          ...prev,
          shouldDisplayError: true,
        };
      });
      return;
    }
  }
}
